import { useMemo } from "react";
import { type Application, type FeatureFlagsResponse, type Shift } from "@/lib/backend/api";
import { useMyApplications } from "@/lib/query/application/use-my-applications";
import { useMyShifts } from "@/lib/query/shift/use-my-shifts";
import { createDayProjectKey } from "@/utils/time";

/**
 * Hook for separated shifts and applications view with deduplication
 *
 * This hook maintains the same business rules as useUpcomingShiftsOverview but returns shifts and
 * applications in separate arrays instead of a merged list.
 *
 * Key Rules:
 *
 * - Applications with status CANCELLATION_REQUESTED are hidden (shown on shift card)
 * - Applications with status CANCELLED are always shown
 * - Applications that have a corresponding shift on the same day+project are hidden (consumed)
 * - Shifts blijven staan tot hun geplande eindtijd, ook als er al uren op zijn ingediend — anders
 *   valt een lopende dienst tussen dit tabblad en Afgerond in
 * - When showOnlyPendingReserveApplicationsInApp is enabled, only PENDING and RESERVE applications
 *   are shown
 * - Applications are hidden if the person has a shift on the same day (Europe/Amsterdam timezone) and
 *   project
 * - Both lists are sorted by start date
 */
export function useSeparatedShiftsAndApplications(featureFlags?: FeatureFlagsResponse) {
  const {
    applications: rawApplications,
    loadMore: loadMoreApplications,
    hasNextPage: hasNextPageApplications,
    refetch: refetchApplications,
  } = useMyApplications(undefined, { fromStartOfDay: true });

  const {
    shifts: rawShifts,
    loadMore: loadMoreShifts,
    hasNextPage: hasNextPageShifts,
    refetch: refetchShifts,
  } = useMyShifts();

  /**
   * Alleen diensten met de gegevens die de kaart nodig heeft.
   *
   * Bewust géén filter op "uren al ingediend". Dat stond hier wel, en het maakte een gat:
   * `useMyShifts` levert alleen diensten met `endAt >= nu`, dus een dienst die nog bezig is stond
   * hier — tot je je uren indiende. Dan viel hij uit dit tabblad, terwijl Afgerond hem nog niet
   * toonde (die eist `endAt < nu`). De dienst was daarmee nergens meer te zien.
   *
   * Dat gat is eerder half gedicht met een `startAt <= nu`-check, voor de spiegelvorm ervan: een
   * toekomstige dienst met door de planner vooraf-ingevulde uren verdween net zo goed. De andere
   * helft — dienst is bezig, medewerker dient zelf uren in — bleef staan.
   *
   * De queries zijn precies complementair (`endAt >= nu` hier, `endAt < nu` bij Afgerond). Elke
   * extra conditie aan één kant breekt dat. Een dienst blijft hier dus staan tot zijn geplande
   * eindtijd voorbij is en Afgerond hem overneemt — ook als er al uren op staan.
   */
  const shifts = useMemo(() => {
    return rawShifts.filter((shift): shift is Shift & { startAt: string; projectId: string } =>
      Boolean(shift?.startAt && shift?.projectId),
    );
  }, [rawShifts]);

  /**
   * In separated mode, show ALL applications (no deduplication) Just filter out invalid ones like
   * CANCELLATION_REQUESTED
   */
  const applications = useMemo(() => {
    // Create a Set of "day+projectId" combinations from shifts for efficient lookup
    const shiftDayProjectSet = new Set<string>();
    shifts.forEach((shift) => {
      if (shift.startAt && shift.projectId) {
        const dayKey = createDayProjectKey(
          shift.startAt,
          shift.projectId,
          shift.timeZone ?? shift.project?.timeZone,
        );
        shiftDayProjectSet.add(dayKey);
      }
    });

    return rawApplications.filter((application) => {
      // Rule 1: Never show CANCELLATION_REQUESTED (info shown on shift card)
      if (application.status === "CANCELLATION_REQUESTED") {
        return false;
      }

      // Rule 2: Check for required data
      const slot = application.availabilitySlot;
      if (!slot?.startAt || !slot?.project?.id) {
        return false;
      }

      // Rule 3: When showOnlyPendingReserveApplicationsInApp is enabled,
      // only show PENDING and RESERVE applications
      if (featureFlags?.showOnlyPendingReserveApplicationsInApp) {
        const isPendingOrReserve =
          application.status === "PENDING" || application.status === "RESERVE";
        if (!isPendingOrReserve) {
          return false;
        }
      }

      // Rule 4: Hide applications if person has a shift on the same day+project (deduplication)
      const dayKey = createDayProjectKey(slot.startAt, slot.project.id, slot.project.timeZone);
      if (shiftDayProjectSet.has(dayKey)) {
        return false;
      }

      return true;
    });
  }, [rawApplications, shifts, featureFlags?.showOnlyPendingReserveApplicationsInApp]);

  // Combined refetch function
  const refetch = async () => {
    await Promise.all([refetchApplications(), refetchShifts()]);
  };

  // Separate load more functions for each tab
  const loadMoreShiftsHandler = async (ev: CustomEvent) => {
    if (hasNextPageShifts) {
      await loadMoreShifts(ev);
    }
    if (ev.target) {
      (ev.target as any).complete();
    }
  };

  const loadMoreApplicationsHandler = async (ev: CustomEvent) => {
    if (hasNextPageApplications) {
      await loadMoreApplications(ev);
    }
    if (ev.target) {
      (ev.target as any).complete();
    }
  };

  return {
    shifts,
    applications,
    loadMoreShifts: loadMoreShiftsHandler,
    loadMoreApplications: loadMoreApplicationsHandler,
    hasNextPageShifts,
    hasNextPageApplications,
    refetch,
  };
}
